import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const { t } = useTranslation('app');
  const location = useLocation();
  const [wellisId, setWellisId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Pre-fill Wellis ID if coming from signup
  useEffect(() => {
    if (location.state?.wellisId) {
      setWellisId(location.state.wellisId);
    }
    if (location.state?.message) {
      setError(''); // Clear any existing errors to show the welcome message instead
    }
  }, [location.state]);

  // Redirect already-logged-in users to dashboard
  if (user) {
    navigate('/dashboard');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ wellis_id: wellisId, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('auth.login.invalidCredentials'));
      }

      localStorage.setItem('token', data.token);
      const userEmail = data.user?.email;
      if (userEmail === 'mjoechia@gmail.com') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError(t('auth.login.googleNotConfigured'));
      return;
    }
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const scope = 'openid email profile';

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent(scope)}`;

    window.location.href = googleAuthUrl;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
      <div className="w-full max-w-md">
        <h1 className="text-headline-lg text-on-background mb-2 text-center">{t('auth.login.title')}</h1>
        <p className="text-body-md text-on-background/60 text-center mb-8">{t('auth.login.subtitle')}</p>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        {location.state?.message && !error && (
          <div className="bg-success/10 border border-success text-success p-4 rounded-lg mb-6 text-body-sm">
            {location.state.message}
          </div>
        )}

        {/* Google Login Button */}
        <button
          onClick={handleGoogleLogin}
          className="w-full bg-surface border border-outline px-4 py-3 rounded-lg text-on-surface font-medium hover:bg-surface/80 transition-colors mb-6 flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.login.googleButton')}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-outline-variant/30"></div>
          <span className="text-body-sm text-on-background/60">{t('auth.login.or')}</span>
          <div className="flex-1 h-px bg-outline-variant/30"></div>
        </div>

        {/* Password Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="wellis_id" className="block text-body-sm text-on-background mb-1">
              {t('auth.login.wellisId')}
            </label>
            <input
              id="wellis_id"
              type="text"
              value={wellisId}
              onChange={(e) => setWellisId(e.target.value.toUpperCase())}
              className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary"
              placeholder="WELLIS-XXXXXXXX"
              required
            />
            <p className="text-body-xs text-on-background/60 mt-1">
              {t('auth.login.wellisIdHelp')}
            </p>
          </div>

          <div>
            <label htmlFor="password" className="block text-body-sm text-on-background mb-1">
              {t('auth.login.password')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 bg-surface border border-outline rounded-lg text-on-surface text-body-sm focus:outline-none focus:border-primary pr-10"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface/60 hover:text-on-surface transition-colors"
                aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
              >
                <span className="material-symbols-outlined text-body-md">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
          >
            {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
          </button>
        </form>

        {/* Links */}
        <div className="mt-6 space-y-2 text-body-sm text-center">
          <Link
            to="/forgot-password"
            className="block text-primary font-medium hover:underline"
          >
            {t('auth.login.forgotPassword')}
          </Link>
          <p className="text-on-background/60">
            {t('auth.login.noAccount')}{' '}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              {t('auth.login.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
